import React from 'react';
import { formatQuoteClientDisplayName } from '../lib/contactConfigurator';
import { getColorById } from '../data/ralColors';
import { isKeralitProfileSystem, profileSystems } from '../data/mockData';
import {
  getKeralitProfileName,
  getWoodJointLabel,
  detectPageType,
  resolveWoodJointId,
} from '../lib/vmkQuoteTable';

const publicUrl = (path) => `${process.env.PUBLIC_URL || ''}${path}`;

/** Opdrachtbon color fallbacks when a drawing has no selection yet. */
const DEFAULT_KADER_RAL = '9010';
const DEFAULT_VLEUGEL_RAL = '7016'; // RAL 7016 antraciet (kleur draaideel)
const DEFAULT_BINNEN_RAL = '9010';

const DOOR_PAGE_TYPES = new Set(['voordeur', 'achterdeur', 'terrasdeur', 'schuifpui']);

/** Empty opdrachtbon / checklist matching the VMK PDF layout. */
export const createEmptyOpdrachtbon = () => ({
  filledBy: '',
  datum: '',
  naam: '',
  adres: '',
  postcode: '',
  woonplaats: '',
  telefoon: '',
  telefoon2: '',
  mail: '',
  merk: '',
  verbinding: '',
  profiel: '',
  montage: null,
  aantalKozijnen: '',
  aantalDeuren: '',
  typeVoordeur: '',
  sloopwerkzaamheden: null,
  sloopM2: '',
  lateien: null,
  container: null,
  afwerkingBinnen: null,
  afwerkingBuiten: null,
  vensterbankBinnen: null,
  kleurKader: '',
  kleurVleugel: '',
  binnenzijde: '',
  typeGlas: '',
  klikHor: null,
  klikHorAantal: '',
  klikHorKleur: '',
  screen: null,
  screenAantal: '',
  screenKleur: '',
  rolluik: null,
  rolluikAantal: '',
  rolluikKleur: '',
  dakraam: null,
  dakraamAantal: '',
  dakraamKleur: '',
  kraan: null,
  kraanHoogte: '',
  tilAssistentie: null,
  tilHoogte: '',
  gevelbekleding: null,
  gevelbekledingM2: '',
  boeidelen: null,
  boeidelenM2: '',
  zetwerk: null,
  zetwerkM2: '',
  dorpelstenen: null,
  dorpelstenenM: '',
  dorpelstenenKleur: '',
  getekendTe: '',
  handtekeningOpdrachtgever: '',
  handtekeningAdviseur: '',
  opmerkingen: '',
  levering: '',
});

const pickFirst = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
};

const isBlank = (value) => value == null || String(value).trim() === '';

const formatOpdrachtbonDate = (value) => {
  if (!value) {
    return new Date().toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
  const raw = String(value).trim();
  // Keep already-local values; convert ISO yyyy-mm-dd to dd-mm-yyyy
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const d = new Date(`${raw.slice(0, 10)}T12:00:00`);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }
  }
  return raw;
};

/** Build client snapshot from quote.clientDetails and/or CRM contact row. */
export const getOpdrachtbonClientSnapshot = (quote, contact = null) => {
  const client = quote?.quote_data?.clientDetails || {};
  const crm = contact || quote?.contact || {};
  const naam = pickFirst(
    formatQuoteClientDisplayName(client),
    client.name,
    client.endClientName,
    [crm.first_name, crm.last_name].filter(Boolean).join(' '),
    crm.name,
  );
  return {
    naam,
    adres: pickFirst(client.address, crm.address),
    postcode: pickFirst(
      client.postalCode,
      client.postal_code,
      crm.postal_code,
      crm.postalCode,
    ),
    woonplaats: pickFirst(client.city, crm.city),
    telefoon: pickFirst(client.phone, crm.phone),
    mail: pickFirst(client.email, crm.email),
  };
};

const collectQuoteDrawings = (quote) => {
  const qd = quote?.quote_data || {};
  const list = [];
  (qd.drawings || []).forEach((d) => list.push(d));
  (qd.sections || []).forEach((section) => {
    (section?.drawings || []).forEach((d) => list.push(d));
  });
  if (!list.length && qd.config) {
    list.push({ config: qd.config, type: qd.type });
  }
  return list;
};

const uniqueJoin = (values) => {
  const seen = new Set();
  const out = [];
  values.forEach((value) => {
    const text = String(value || '').trim();
    if (!text || seen.has(text)) return;
    seen.add(text);
    out.push(text);
  });
  return out.join(' / ');
};

const formatOpdrachtbonColor = (colorId, fallbackRal) => {
  const color = colorId ? getColorById(colorId) : null;
  const ral = String(color?.ral || fallbackRal || '').replace(/^0+/, '');
  return ral || '';
};

const getProfileSystemLabel = (profileSystemId) => {
  if (!profileSystemId) return '';
  if (isKeralitProfileSystem(profileSystemId)) {
    return getKeralitProfileName(profileSystemId) || String(profileSystemId);
  }
  const profile = profileSystems.find((p) => p.id === profileSystemId);
  return profile?.name || String(profileSystemId);
};

/** Merk / verbinding / uitvoering colors from quote drawings. */
export const getOpdrachtbonConfigSnapshot = (quote) => {
  const drawings = collectQuoteDrawings(quote);
  const configs = drawings
    .map((d) => d?.config)
    .filter((c) => c && typeof c === 'object');

  // Prefer window/door configs over cladding for frame colors / verbinding
  const frameConfigs = configs.filter(
    (c) => !isKeralitProfileSystem(c.profileSystem),
  );
  const colorConfigs = frameConfigs.length ? frameConfigs : configs;
  const merkConfigs = frameConfigs.length ? frameConfigs : configs;
  const jointConfigs = frameConfigs.length ? frameConfigs : configs;

  const merk = uniqueJoin(
    merkConfigs.map((c) => getProfileSystemLabel(c.profileSystem)).filter(Boolean),
  );

  const verbinding = uniqueJoin(
    jointConfigs.map((c) => getWoodJointLabel(resolveWoodJointId(c))),
  );

  // Colors: use first frame drawing (same order as quote), fall back to defaults
  const primary = colorConfigs[0] || null;
  const exteriorId = primary
    ? primary.exteriorColor || primary.color || null
    : null;
  const draaideelId = primary?.draaideelColor || null;
  const interiorId = primary
    ? primary.interiorFrameColor || primary.interiorColor || null
    : null;

  let aantalKozijnen = 0;
  let aantalDeuren = 0;
  const voordeurTypes = [];

  drawings.forEach((drawing) => {
    const config = drawing?.config || {};
    const type = drawing?.type || detectPageType(config);
    const qty = Math.max(1, Number(config.quantity) || 1);
    if (type === 'keralit' || type === 'plastisol' || type === 'dakkapel') return;
    if (DOOR_PAGE_TYPES.has(type)) {
      aantalDeuren += qty;
      if (type === 'voordeur') {
        const style = config.doorStyle || config.voordeurType || '';
        if (style) voordeurTypes.push(String(style));
      }
    } else if (type === 'kozijnen') {
      aantalKozijnen += qty;
    }
  });

  return {
    merk,
    verbinding,
    kleurKader: formatOpdrachtbonColor(exteriorId, DEFAULT_KADER_RAL),
    kleurVleugel: formatOpdrachtbonColor(draaideelId, DEFAULT_VLEUGEL_RAL),
    binnenzijde: formatOpdrachtbonColor(interiorId, DEFAULT_BINNEN_RAL),
    aantalKozijnen: aantalKozijnen > 0 ? String(aantalKozijnen) : '',
    aantalDeuren: aantalDeuren > 0 ? String(aantalDeuren) : '',
    typeVoordeur: uniqueJoin(voordeurTypes),
  };
};

/**
 * Prefill opdrachtbon from quote client + drawing config (+ optional CRM contact).
 * Existing saved values win; blank fields are filled from the quote.
 * @param {object} [options]
 * @param {string} [options.filledBy] — "Created by" / logged-in user display name
 */
export const prefillOpdrachtbonFromQuote = (quote, contact = null, options = {}) => {
  const base = createEmptyOpdrachtbon();
  const existing =
    quote?.quote_data?.opdrachtbon && typeof quote.quote_data.opdrachtbon === 'object'
      ? quote.quote_data.opdrachtbon
      : {};
  const client = getOpdrachtbonClientSnapshot(quote, contact);
  const configSnap = getOpdrachtbonConfigSnapshot(quote);

  const merged = { ...base, ...existing };
  const fillIfBlank = (key, value) => {
    if (isBlank(merged[key]) && value) merged[key] = value;
  };

  merged.datum = isBlank(merged.datum)
    ? formatOpdrachtbonDate()
    : formatOpdrachtbonDate(merged.datum);

  const creatorFromQuote = pickFirst(
    options.filledBy,
    (() => {
      const stored = quote?.quote_data?.quoteCreator;
      if (!stored || typeof stored !== 'object') return '';
      return (
        `${stored.first_name || ''} ${stored.last_name || ''}`.trim() ||
        stored.email ||
        ''
      );
    })(),
    `${quote?.creator_first_name || ''} ${quote?.creator_last_name || ''}`.trim(),
    quote?.creator_email,
  );
  fillIfBlank('filledBy', creatorFromQuote);

  fillIfBlank('naam', client.naam);
  fillIfBlank('adres', client.adres);
  fillIfBlank('postcode', client.postcode);
  fillIfBlank('woonplaats', client.woonplaats);
  fillIfBlank('telefoon', client.telefoon);
  fillIfBlank('mail', client.mail);

  fillIfBlank('merk', configSnap.merk);
  fillIfBlank('verbinding', configSnap.verbinding);

  // Color fields: prefer RAL codes; replace older name-based values
  const fillColorCode = (key, value) => {
    if (!value) return;
    const current = String(merged[key] ?? '').trim();
    if (!current || !/^\d{3,4}$/.test(current)) {
      merged[key] = value;
    }
  };
  fillColorCode('kleurKader', configSnap.kleurKader);
  fillColorCode('kleurVleugel', configSnap.kleurVleugel);
  fillColorCode('binnenzijde', configSnap.binnenzijde);

  fillIfBlank('aantalKozijnen', configSnap.aantalKozijnen);
  fillIfBlank('aantalDeuren', configSnap.aantalDeuren);
  fillIfBlank('typeVoordeur', configSnap.typeVoordeur);

  return merged;
};

const JaNee = ({ value, onChange, labels = { ja: 'JA', nee: 'NEE' } }) => (
  <span className="inline-flex items-center gap-1 text-[11px] font-semibold tracking-wide">
    <button
      type="button"
      onClick={() => onChange(true)}
      className={`rounded px-1.5 py-0.5 ${
        value === true ? 'bg-[#5B2C8A] text-white' : 'text-[#5B2C8A] hover:bg-purple-50'
      }`}
    >
      {labels.ja}
    </button>
    <span className="text-gray-400">/</span>
    <button
      type="button"
      onClick={() => onChange(false)}
      className={`rounded px-1.5 py-0.5 ${
        value === false ? 'bg-[#5B2C8A] text-white' : 'text-[#5B2C8A] hover:bg-purple-50'
      }`}
    >
      {labels.nee}
    </button>
  </span>
);

const DottedInput = ({ value, onChange, className = '', placeholder = '' }) => (
  <input
    type="text"
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className={`min-w-0 flex-1 border-0 border-b border-dotted border-[#5B8DEF] bg-transparent px-1 py-0.5 text-sm text-gray-900 outline-none focus:border-solid focus:border-[#5B2C8A] ${className}`}
  />
);

const FieldRow = ({ label, children, className = '' }) => (
  <div className={`flex items-baseline gap-2 ${className}`}>
    <span className="shrink-0 text-sm font-medium text-gray-800">{label}</span>
    <span className="shrink-0 text-sm text-gray-500">:</span>
    {children}
  </div>
);

/**
 * Editable VMK Opdrachtbon / checklist — layout matches the paper PDF.
 */
const OpdrachtbonEditor = ({ value, onChange, quoteNumber }) => {
  const data = value || createEmptyOpdrachtbon();
  const set = (key, next) => onChange({ ...data, [key]: next });

  return (
    <div className="opdrachtbon-sheet mx-auto max-w-[900px] bg-white text-[#1a1a1a]">
      {/* Header — same VMK logo as export PDF */}
      <div className="flex items-start justify-between gap-4 border-b border-gray-200 pb-4">
        <div className="flex min-w-0 flex-col gap-1">
          <img
            src={publicUrl('/images/vmk-kozijnen-logo.png')}
            alt="VMK Kozijnen"
            className="h-14 w-auto max-w-[220px] object-contain object-left"
          />
          {quoteNumber ? (
            <div className="text-xs font-medium text-gray-500">{quoteNumber}</div>
          ) : null}
        </div>
        <div className="shrink-0 text-right text-[11px] leading-relaxed text-gray-600">
          <div className="font-semibold text-gray-800">VMK Kozijnen</div>
          <div>Binnenhavenstraat 41</div>
          <div>7553 GH Hengelo (ov)</div>
          <div>info@vmkkozijnen.nl</div>
          <div>074 234 03 33</div>
          <div>KVK | 81936974</div>
          <div>BTW | NL862276652B01</div>
        </div>
      </div>

      <div className="mt-4 flex items-baseline gap-2">
        <span className="text-sm font-semibold text-gray-800">Checklist ingevuld door:</span>
        <DottedInput value={data.filledBy} onChange={(v) => set('filledBy', v)} />
      </div>

      {/* Main two columns */}
      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* Left */}
        <div className="space-y-4 rounded border-2 border-[#5B2C8A] p-3">
          <div className="space-y-2">
            <FieldRow label="Datum">
              <DottedInput value={data.datum} onChange={(v) => set('datum', v)} />
            </FieldRow>
            <FieldRow label="Naam">
              <DottedInput value={data.naam} onChange={(v) => set('naam', v)} />
            </FieldRow>
            <FieldRow label="Adres">
              <DottedInput value={data.adres} onChange={(v) => set('adres', v)} />
            </FieldRow>
            <FieldRow label="Postcode">
              <DottedInput value={data.postcode} onChange={(v) => set('postcode', v)} />
            </FieldRow>
            <FieldRow label="Woonplaats">
              <DottedInput value={data.woonplaats} onChange={(v) => set('woonplaats', v)} />
            </FieldRow>
            <FieldRow label="Telefoon" className="flex-wrap">
              <DottedInput
                value={data.telefoon}
                onChange={(v) => set('telefoon', v)}
              />
              <span className="shrink-0 px-1 text-sm font-semibold text-gray-500">|</span>
              <DottedInput
                value={data.telefoon2}
                onChange={(v) => set('telefoon2', v)}
              />
            </FieldRow>
            <FieldRow label="Mail">
              <DottedInput value={data.mail} onChange={(v) => set('mail', v)} />
            </FieldRow>
          </div>

          <div>
            <div className="mb-2 text-sm font-bold text-[#5B2C8A]">Technische specificatie</div>
            <div className="space-y-2">
              <FieldRow label="Merk">
                <DottedInput
                  value={data.merk}
                  onChange={(v) => set('merk', v)}
                />
              </FieldRow>
              <FieldRow label="Verbinding">
                <DottedInput
                  value={data.verbinding}
                  onChange={(v) => set('verbinding', v)}
                />
              </FieldRow>
              <FieldRow label="Profiel">
                <DottedInput
                  value={data.profiel}
                  onChange={(v) => set('profiel', v)}
                />
              </FieldRow>
            </div>
          </div>
        </div>

        {/* Right */}
        <div className="space-y-3 rounded border-2 border-[#5B2C8A] p-3">
          <div className="text-sm font-bold text-[#5B2C8A]">Montage</div>
          <FieldRow label="Montage">
            <JaNee value={data.montage} onChange={(v) => set('montage', v)} />
          </FieldRow>
          <FieldRow label="Aantal kozijnen">
            <DottedInput value={data.aantalKozijnen} onChange={(v) => set('aantalKozijnen', v)} />
          </FieldRow>
          <FieldRow label="Aantal deuren">
            <DottedInput value={data.aantalDeuren} onChange={(v) => set('aantalDeuren', v)} />
          </FieldRow>
          <FieldRow label="Type voordeur">
            <DottedInput value={data.typeVoordeur} onChange={(v) => set('typeVoordeur', v)} />
          </FieldRow>
          <FieldRow label="Sloopwerkzaamheden">
            <JaNee value={data.sloopwerkzaamheden} onChange={(v) => set('sloopwerkzaamheden', v)} />
            <DottedInput
              value={data.sloopM2}
              onChange={(v) => set('sloopM2', v)}
              className="max-w-[80px]"
              placeholder="m²"
            />
          </FieldRow>
          <FieldRow label="Lateien aanbrengen">
            <JaNee value={data.lateien} onChange={(v) => set('lateien', v)} />
          </FieldRow>
          <FieldRow label="Container">
            <JaNee value={data.container} onChange={(v) => set('container', v)} />
          </FieldRow>

          <div className="border-t border-purple-100 pt-2 text-sm font-bold text-[#5B2C8A]">
            Montage
          </div>
          <FieldRow label="Afwerking binnenzijde">
            <JaNee value={data.afwerkingBinnen} onChange={(v) => set('afwerkingBinnen', v)} />
          </FieldRow>
          <FieldRow label="Afwerking buitenzijde">
            <JaNee value={data.afwerkingBuiten} onChange={(v) => set('afwerkingBuiten', v)} />
          </FieldRow>
          <FieldRow label="Vensterbank binnenzijde">
            <JaNee value={data.vensterbankBinnen} onChange={(v) => set('vensterbankBinnen', v)} />
          </FieldRow>

          <div className="border-t border-purple-100 pt-2 text-sm font-bold text-[#5B2C8A]">
            Uitvoering
          </div>
          <FieldRow label="Kleur kozijn kader">
            <DottedInput value={data.kleurKader} onChange={(v) => set('kleurKader', v)} />
            <span className="shrink-0 text-xs text-gray-500">vleugel</span>
            <DottedInput
              value={data.kleurVleugel}
              onChange={(v) => set('kleurVleugel', v)}
              className="max-w-[120px]"
            />
          </FieldRow>
          <FieldRow label="Binnenzijde">
            <DottedInput value={data.binnenzijde} onChange={(v) => set('binnenzijde', v)} />
          </FieldRow>
          <FieldRow label="Type glas">
            <DottedInput value={data.typeGlas} onChange={(v) => set('typeGlas', v)} />
          </FieldRow>
        </div>
      </div>

      {/* Extra options + lift support */}
      <div className="mt-3 grid grid-cols-1 gap-3 rounded border-2 border-[#5B2C8A] p-3 md:grid-cols-2">
        <div className="space-y-2">
          <div className="text-sm font-bold text-[#5B2C8A]">Extra opties</div>
          <FieldRow label="Klik hor op kleur en maat" className="flex-wrap">
            <JaNee value={data.klikHor} onChange={(v) => set('klikHor', v)} />
            <span className="text-xs text-gray-500">aantal</span>
            <DottedInput
              value={data.klikHorAantal}
              onChange={(v) => set('klikHorAantal', v)}
              className="max-w-[60px]"
            />
            <span className="text-xs text-gray-500">kleur</span>
            <DottedInput
              value={data.klikHorKleur}
              onChange={(v) => set('klikHorKleur', v)}
              className="max-w-[100px]"
            />
          </FieldRow>
          <FieldRow label="Screen elektrisch bediend" className="flex-wrap">
            <JaNee value={data.screen} onChange={(v) => set('screen', v)} />
            <span className="text-xs text-gray-500">aantal</span>
            <DottedInput
              value={data.screenAantal}
              onChange={(v) => set('screenAantal', v)}
              className="max-w-[60px]"
            />
            <span className="text-xs text-gray-500">kleur</span>
            <DottedInput
              value={data.screenKleur}
              onChange={(v) => set('screenKleur', v)}
              className="max-w-[100px]"
            />
          </FieldRow>
          <FieldRow label="Rolluik elektrisch bediend" className="flex-wrap">
            <JaNee value={data.rolluik} onChange={(v) => set('rolluik', v)} />
            <span className="text-xs text-gray-500">aantal</span>
            <DottedInput
              value={data.rolluikAantal}
              onChange={(v) => set('rolluikAantal', v)}
              className="max-w-[60px]"
            />
            <span className="text-xs text-gray-500">kleur</span>
            <DottedInput
              value={data.rolluikKleur}
              onChange={(v) => set('rolluikKleur', v)}
              className="max-w-[100px]"
            />
          </FieldRow>
          <FieldRow label="Dakraam" className="flex-wrap">
            <JaNee value={data.dakraam} onChange={(v) => set('dakraam', v)} />
            <span className="text-xs text-gray-500">aantal</span>
            <DottedInput
              value={data.dakraamAantal}
              onChange={(v) => set('dakraamAantal', v)}
              className="max-w-[60px]"
            />
            <span className="text-xs text-gray-500">kleur</span>
            <DottedInput
              value={data.dakraamKleur}
              onChange={(v) => set('dakraamKleur', v)}
              className="max-w-[100px]"
            />
          </FieldRow>
        </div>
        <div className="space-y-2">
          <div className="text-sm font-bold text-[#5B2C8A]">Til- / hefondersteuning</div>
          <FieldRow label="Kraan" className="flex-wrap">
            <JaNee value={data.kraan} onChange={(v) => set('kraan', v)} />
            <DottedInput
              value={data.kraanHoogte}
              onChange={(v) => set('kraanHoogte', v)}
              className="max-w-[80px]"
            />
            <span className="text-xs text-gray-500">m hoog</span>
          </FieldRow>
          <FieldRow label="Til-assistentie" className="flex-wrap">
            <JaNee value={data.tilAssistentie} onChange={(v) => set('tilAssistentie', v)} />
            <DottedInput
              value={data.tilHoogte}
              onChange={(v) => set('tilHoogte', v)}
              className="max-w-[80px]"
            />
            <span className="text-xs text-gray-500">m hoog</span>
          </FieldRow>
        </div>
      </div>

      {/* Woningafwerking */}
      <div className="mt-3 space-y-2 rounded border-2 border-[#5B2C8A] p-3">
        <div className="text-sm font-bold text-[#5B2C8A]">Woningafwerking</div>
        <FieldRow label="Gevelbekleding" className="flex-wrap">
          <JaNee value={data.gevelbekleding} onChange={(v) => set('gevelbekleding', v)} />
          <DottedInput
            value={data.gevelbekledingM2}
            onChange={(v) => set('gevelbekledingM2', v)}
            className="max-w-[80px]"
          />
          <span className="text-xs text-gray-500">m²</span>
        </FieldRow>
        <FieldRow label="Boeidelen" className="flex-wrap">
          <JaNee value={data.boeidelen} onChange={(v) => set('boeidelen', v)} />
          <DottedInput
            value={data.boeidelenM2}
            onChange={(v) => set('boeidelenM2', v)}
            className="max-w-[80px]"
          />
          <span className="text-xs text-gray-500">m²</span>
        </FieldRow>
        <FieldRow label="Zetwerk" className="flex-wrap">
          <JaNee value={data.zetwerk} onChange={(v) => set('zetwerk', v)} />
          <DottedInput
            value={data.zetwerkM2}
            onChange={(v) => set('zetwerkM2', v)}
            className="max-w-[80px]"
          />
          <span className="text-xs text-gray-500">m²</span>
        </FieldRow>
        <FieldRow label="Dorpelstenen" className="flex-wrap">
          <JaNee value={data.dorpelstenen} onChange={(v) => set('dorpelstenen', v)} />
          <DottedInput
            value={data.dorpelstenenM}
            onChange={(v) => set('dorpelstenenM', v)}
            className="max-w-[80px]"
          />
          <span className="text-xs text-gray-500">m</span>
          <span className="text-xs text-gray-500">kleur</span>
          <DottedInput
            value={data.dorpelstenenKleur}
            onChange={(v) => set('dorpelstenenKleur', v)}
            className="max-w-[120px]"
          />
        </FieldRow>
      </div>

      {/* Akkoord + Opmerkingen */}
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-3 rounded border-2 border-[#5B2C8A] p-3">
          <div className="text-sm font-bold text-[#5B2C8A]">Akkoord</div>
          <FieldRow label="Getekend te">
            <DottedInput value={data.getekendTe} onChange={(v) => set('getekendTe', v)} />
          </FieldRow>
          <FieldRow label="Handtekening opdrachtgever">
            <DottedInput
              value={data.handtekeningOpdrachtgever}
              onChange={(v) => set('handtekeningOpdrachtgever', v)}
            />
          </FieldRow>
          <FieldRow label="Handtekening adviseur">
            <DottedInput
              value={data.handtekeningAdviseur}
              onChange={(v) => set('handtekeningAdviseur', v)}
            />
          </FieldRow>
        </div>
        <div className="rounded border-2 border-[#5B2C8A] p-3">
          <div className="mb-2 text-sm font-bold text-[#5B2C8A]">Opmerkingen</div>
          <textarea
            value={data.opmerkingen ?? ''}
            onChange={(e) => set('opmerkingen', e.target.value)}
            rows={6}
            className="w-full resize-y rounded border border-dotted border-[#5B8DEF] bg-transparent p-2 text-sm outline-none focus:border-solid focus:border-[#5B2C8A]"
          />
        </div>
      </div>

      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-sm font-semibold">Levering: Z.S.M. of</span>
        <DottedInput value={data.levering} onChange={(v) => set('levering', v)} />
      </div>

      <p className="mt-4 text-[10px] leading-relaxed text-gray-500">
        Op al onze aanbiedingen en overeenkomsten zijn onze algemene voorwaarden van toepassing.
        Deze zijn te vinden op onze website en worden op verzoek toegezonden. Door ondertekening
        van deze opdrachtbon verklaart de opdrachtgever akkoord te gaan met de gestelde voorwaarden
        en specificaties.
      </p>
    </div>
  );
};

export default OpdrachtbonEditor;
